import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const PLACE_CONFIG = {
  tenths: { step: 0.1, label: "części dziesiątych" },
  hundredths: { step: 0.01, label: "części setnych" },
  ones: { step: 1, label: "jedności" },
  tens: { step: 10, label: "dziesiątek" },
  hundreds: { step: 100, label: "setek" },
  thousands: { step: 1000, label: "tysięcy" },
};

const DECISION_DIGIT_LABEL = {
  tenths: "cyfra setnych",
  hundredths: "cyfra tysięcznych",
  ones: "cyfra części dziesiątych",
  tens: "cyfra jedności",
  hundreds: "cyfra dziesiątek",
  thousands: "cyfra setek",
};

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(items) {
  return items[randInt(0, items.length - 1)];
}

function roundToStep(value, step) {
  return Math.round((value + Number.EPSILON) / step) * step;
}

function normalizeNumber(value) {
  const rounded = Math.round((Number(value) + Number.EPSILON) * 10000) / 10000;
  return Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(".", ",");
}

function latexNumber(value) {
  return normalizeNumber(value).replace(",", "{,}");
}

function highlightedDecisionNumber(data) {
  const text = normalizeNumber(data.value);
  const [integerPart, decimalPart = ""] = text.split(",");
  const color = "#ff9800";

  const highlightDecimal = (index) => {
    if (index < 0 || index >= decimalPart.length) return latexNumber(data.value);
    return `${integerPart}{,}${decimalPart.slice(0, index)}{\\color{${color}}{${decimalPart[index]}}}${decimalPart.slice(index + 1)}`;
  };

  const highlightInteger = (indexFromRight) => {
    const index = integerPart.length - indexFromRight;
    if (index < 0 || index >= integerPart.length) return latexNumber(data.value);
    const decimalSuffix = decimalPart ? `{,}${decimalPart}` : "";
    return `${integerPart.slice(0, index)}{\\color{${color}}{${integerPart[index]}}}${integerPart.slice(index + 1)}${decimalSuffix}`;
  };

  if (data.place === "tenths") return highlightDecimal(1);
  if (data.place === "hundredths") return highlightDecimal(2);
  if (data.place === "ones") return highlightDecimal(0);
  if (data.place === "tens") return highlightInteger(1);
  if (data.place === "hundreds") return highlightInteger(2);
  if (data.place === "thousands") return highlightInteger(3);
  return latexNumber(data.value);
}


function makeDecimalFromDigits(whole, digits) {
  return Number(`${whole}.${digits.join("")}`);
}

function makeFineDecimal(place) {
  const whole = randInt(2, 98);

  if (place === "tenths") {
    const decimalPlaces = pickOne([2, 3]);
    const tenths = randInt(0, 9);
    const hundredths = randInt(1, 9);
    const digits = [tenths, hundredths];
    if (decimalPlaces === 3) digits.push(randInt(1, 9));
    return makeDecimalFromDigits(whole, digits);
  }

  const decimalPlaces = pickOne([3, 4]);
  const tenths = randInt(0, 9);
  const hundredths = randInt(0, 9);
  const thousandths = randInt(1, 9);
  const digits = [tenths, hundredths, thousandths];
  if (decimalPlaces === 4) digits.push(randInt(1, 9));
  return makeDecimalFromDigits(whole, digits);
}

function makeOnesValue() {
  const whole = randInt(12, 980);
  const tenths = randInt(1, 9);
  const hundredths = randInt(1, 9);
  return makeDecimalFromDigits(whole, [tenths, hundredths]);
}

function makeLargeValue(place) {
  const ranges = {
    tens: [120, 9800],
    hundreds: [1250, 98750],
    thousands: [12500, 987650],
  };
  const [min, max] = ranges[place] || ranges.tens;
  const integer = randInt(min, max);
  const withDecimal = Math.random() < 0.45;
  if (!withDecimal) return integer;
  return integer + pickOne([randInt(1, 9) / 10, randInt(11, 99) / 100]);
}

function makeValue(place, step) {
  for (let i = 0; i < 120; i += 1) {
    const value =
      place === "tenths" || place === "hundredths"
        ? makeFineDecimal(place)
        : place === "ones"
          ? makeOnesValue()
          : makeLargeValue(place);
    const answer = roundToStep(value, step);
    if (Math.abs(answer - value) > 1e-9) return { value, answer };
  }

  const fallback = step + step / 2;
  return { value: fallback, answer: roundToStep(fallback, step) };
}

function decisionDigit(data) {
  const absValue = Math.abs(Number(data.value));
  if (data.place === "tenths") return Math.floor(absValue * 100 + 1e-6) % 10;
  if (data.place === "hundredths") return Math.floor(absValue * 1000 + 1e-6) % 10;
  if (data.place === "ones") return Math.floor(absValue * 10 + 1e-6) % 10;
  if (data.place === "tens") return Math.floor(absValue) % 10;
  if (data.place === "hundreds") return Math.floor(absValue / 10) % 10;
  if (data.place === "thousands") return Math.floor(absValue / 100) % 10;
  return 0;
}

function decisionDirection(digit) {
  return digit >= 5 ? "w górę" : "w dół";
}

export function generateTask(options = {}) {
  const place =
    options.place ||
    pickOne(["tenths", "hundredths", "ones", "tens", "hundreds", "thousands"]);
  const config = PLACE_CONFIG[place] || PLACE_CONFIG.ones;
  const { value, answer } = makeValue(place, config.step);

  return {
    type: "DecimalRoundingTask",
    data: {
      value,
      answer,
      place,
      label: config.label,
      step: config.step,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Zaokrąglij liczbę <InlineMath math={latexNumber(data.value)} /> do{" "}
        {data.label}.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. 12,3 lub 120"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - task.data.answer) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={latexNumber(data.answer)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ data }) {
  const digit = decisionDigit(data);
  const digitLabel = DECISION_DIGIT_LABEL[data.place] || "cyfra decydująca";

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Patrzymy na cyfrę stojącą za rzędem, do którego
        zaokrąglamy.
      </p>
      <p>
        W tym zadaniu jest to {digitLabel}: {" "}
        <InlineMath math={`${digit}`} />.
      </p>
      <p>
        Jeżeli cyfra decydująca jest mniejsza od <InlineMath math="5" />, zaokrąglamy
        w dół. Jeżeli jest równa <InlineMath math="5" /> albo większa, zaokrąglamy
        w górę.
      </p>
      <p>
        <strong>Krok 2.</strong> Cyfra decydująca to {" "}
        <InlineMath math={`${digit}`} />, więc zaokrąglamy{" "}
        {decisionDirection(digit)}:
      </p>
      <p style={{ textAlign: "center" }}>
        <InlineMath
          math={`${highlightedDecisionNumber(data)} \\approx ${latexNumber(data.answer)}`}
        />
      </p>
      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={latexNumber(data.answer)} />.
      </p>
    </div>
  );
}
